import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { bookingAPI, tripAPI } from '../services/api';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const downloadTicketPDF = async (bookingId, bookingNumber) => {
  try {
    const res = await fetch(`${API_BASE}/bookings/${bookingId}/ticket`, {
      credentials: 'include',
    });
    if (!res.ok) {
      let msg = 'تعذّر تنزيل التذكرة';
      try { const j = await res.json(); msg = j.message || msg; } catch {}
      throw new Error(msg);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = Object.assign(document.createElement('a'), {
      href: url,
      download: `تذكرة-${bookingNumber}.pdf`,
    });
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    toast.error(err.message || 'تعذّر تنزيل التذكرة');
  }
};

// Fetch a receipt through the authenticated /api/receipts endpoint and open it
// as a blob URL. The fetch always uses an absolute Render URL so it bypasses
// Netlify's SPA catch-all which would return index.html for relative /api/* paths.
// We also open the new tab synchronously (user-gesture context) to avoid popup blockers.
const openReceipt = async (rawUrl) => {
  if (!rawUrl) return;

  // Build absolute URL to Render backend.
  // rawUrl is either:
  //   a) Full API URL already: "https://api.safer-sdn.com/api/receipts/receipt-xxx.jpg"
  //   b) Legacy upload path:      "/uploads/receipt-xxx.jpg"
  let fetchUrl;
  if (rawUrl.startsWith('http')) {
    fetchUrl = rawUrl;
  } else {
    const filename = rawUrl.replace(/^.*\/uploads\//, '');
    fetchUrl = `${API_BASE}/receipts/${filename}`;
  }

  // Open tab NOW while in the click-handler call stack so browser doesn't block it
  const win = window.open('', '_blank');
  try {
    const res = await fetch(fetchUrl, { credentials: 'include' });
    if (!res.ok) throw new Error(`server error ${res.status}`);
    const ct = res.headers.get('Content-Type') || '';
    if (!ct.match(/image|pdf|octet-stream/i)) throw new Error('unexpected response type');
    const blob    = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    if (win) win.location.href = blobUrl;
    setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);
  } catch {
    if (win) win.close();
    toast.error('تعذّر فتح الإيصال');
  }
};

const REFRESH_INTERVAL = 30_000;

const BookingsPage = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [confirmedBookings, setConfirmedBookings] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [showOfficeModal, setShowOfficeModal]   = useState(false);
  const [officeTrips, setOfficeTrips]           = useState([]);
  const [officeTripsLoading, setOfficeTripsLoading] = useState(false);
  const [selectedTripData, setSelectedTripData] = useState(null);
  const [officeForm, setOfficeForm]             = useState({ tripId: '', seats: [], passengerDetails: [], seatCount: '' });
  const [officeSubmitting, setOfficeSubmitting] = useState(false);

  const fetchBookings = (silent = false) => {
    if (!silent) setLoading(true);
    bookingAPI.getPending()
      .then((res) => { setBookings(res.data.data); setLastRefreshed(new Date()); })
      .catch(() => { if (!silent) toast.error('فشل في تحميل الحجوزات'); })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchBookings();
    let timer = setInterval(() => fetchBookings(true), REFRESH_INTERVAL);

    const handleVisibility = () => {
      if (document.hidden) {
        clearInterval(timer);
      } else {
        fetchBookings(true);
        timer = setInterval(() => fetchBookings(true), REFRESH_INTERVAL);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  const handleConfirm = async (id) => {
    const booking = bookings.find((b) => b.id === id);
    try {
      await bookingAPI.confirm(id);
      toast.success('تم تأكيد الحجز وإرسال التذكرة عبر واتساب!');
      if (booking) {
        setConfirmedBookings((prev) => [...prev, { ...booking, confirmedAt: Date.now() }]);
      }
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.message || 'فشل في التأكيد');
    }
  };

  const handleDownload = async (id, bookingNumber) => {
    setDownloadingId(id);
    await downloadTicketPDF(id, bookingNumber);
    setDownloadingId(null);
  };

  const dismissConfirmed = (id) => setConfirmedBookings((prev) => prev.filter((b) => b.id !== id));

  const openRejectModal = (booking) => {
    setSelected(booking);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    try {
      await bookingAPI.reject(selected.id, { reason: rejectionReason });
      toast.success('تم رفض الحجز وتحرير المقاعد');
      setShowRejectModal(false);
      fetchBookings();
    } catch {
      toast.error('فشل في الرفض');
    }
  };

  const openOfficeModal = async () => {
    setShowOfficeModal(true);
    setOfficeTripsLoading(true);
    setOfficeForm({ tripId: '', seats: [], passengerDetails: [], seatCount: '' });
    setSelectedTripData(null);
    try {
      const res = await tripAPI.getCompanyTrips();
      const now = new Date();
      setOfficeTrips((res.data.data || []).filter((t) =>
        t.status === 'scheduled' && t.availableSeats > 0 && new Date(t.departureTime) > now
      ));
    } catch {
      toast.error('فشل في تحميل الرحلات');
    } finally {
      setOfficeTripsLoading(false);
    }
  };

  const handleTripSelect = (tripId) => {
    const trip = officeTrips.find((t) => t.id === tripId) || null;
    setSelectedTripData(trip);
    setOfficeForm({ tripId, seats: [], passengerDetails: [], seatCount: '' });
  };

  const handleSeatCount = (countStr, trip) => {
    const count = parseInt(countStr, 10);
    const tripData = trip || selectedTripData;
    if (!tripData || isNaN(count) || count < 1) {
      setOfficeForm((f) => ({ ...f, seatCount: countStr, seats: [], passengerDetails: [] }));
      return;
    }
    const totalSeats = tripData.bus?.totalSeats || 43;
    const available = [];
    for (let i = 1; i <= totalSeats && available.length < count; i++) {
      if (!tripData.bookedSeats?.includes(i)) available.push(i);
    }
    const details = available.map((s) => ({ seat: s, name: '', phone: '', whatsapp: '' }));
    setOfficeForm((f) => ({ ...f, seatCount: countStr, seats: available, passengerDetails: details }));
  };

  const toggleOfficeSeat = (seatNum) => {
    setOfficeForm((f) => {
      const already = f.seats.includes(seatNum);
      const newSeats = already ? f.seats.filter((s) => s !== seatNum) : [...f.seats, seatNum];
      const newDetails = newSeats.map((s) => {
        const existing = f.passengerDetails.find((pd) => pd.seat === s);
        return existing || { seat: s, name: '', phone: '', whatsapp: '' };
      });
      return { ...f, seats: newSeats, passengerDetails: newDetails, seatCount: String(newSeats.length) };
    });
  };

  const updateOfficePassenger = (seat, field, value) => {
    setOfficeForm((f) => ({
      ...f,
      passengerDetails: f.passengerDetails.map((pd) =>
        pd.seat === seat ? { ...pd, [field]: value } : pd
      ),
    }));
  };

  const handleOfficeSubmit = async () => {
    if (!officeForm.tripId)            return toast.error('يرجى اختيار رحلة');
    if (officeForm.seats.length === 0) return toast.error('يرجى تحديد عدد المقاعد');
    const missingInfo = officeForm.passengerDetails.some((pd) => !pd.name.trim() || !pd.phone.trim());
    if (missingInfo) return toast.error('يرجى إدخال الاسم رباعياً ورقم الهاتف لجميع المسافرين');
    setOfficeSubmitting(true);
    try {
      const res = await bookingAPI.createOffice({
        tripId: officeForm.tripId,
        seats: officeForm.seats,
        passengerDetails: officeForm.passengerDetails,
      });
      toast.success(`تم الحجز المكتبي ✅ رقم الحجز: ${res.data.data.bookingNumber}`);
      setShowOfficeModal(false);
      fetchBookings();
    } catch (e) {
      toast.error(e.response?.data?.message || 'فشل في إنشاء الحجز');
    } finally {
      setOfficeSubmitting(false);
    }
  };

  return (
    <div dir="rtl" className="p-4 md:p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800">مراجعة الحجوزات والإيصالات</h1>
          <p className="text-gray-500 text-sm">الحجوزات التي بانتظار التحقق من الإيصال البنكي</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {lastRefreshed && (
            <span className="text-xs text-gray-400">
              آخر تحديث: {lastRefreshed.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button
            onClick={openOfficeModal}
            className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded-lg text-sm font-semibold transition"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            حجز مكتبي
          </button>
          <button
            onClick={() => fetchBookings()}
            disabled={loading}
            className="flex items-center gap-1.5 bg-blue-50 hover:bg-blue-100 disabled:opacity-50 text-blue-700 border border-blue-200 px-3 py-1.5 rounded-lg text-sm font-medium transition"
          >
            <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            تحديث
          </button>
        </div>
      </div>

      {/* Recently confirmed — shows download button for just-issued tickets */}
      {confirmedBookings.length > 0 && (
        <div className="space-y-2">
          {confirmedBookings.map((b) => {
            const names = b.passengerDetails?.length > 0
              ? b.passengerDetails.map((pd) => pd.name).join('، ')
              : b.passenger?.name || '—';
            return (
              <div key={b.id} className="flex items-center justify-between gap-4 bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-xl flex-shrink-0">✅</span>
                  <div className="min-w-0">
                    <p className="font-bold text-emerald-800 text-sm">{b.bookingNumber}</p>
                    <p className="text-emerald-700 text-xs truncate">{names}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleDownload(b.id, b.bookingNumber)}
                    disabled={downloadingId === b.id}
                    className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white px-4 py-1.5 rounded-lg text-sm font-semibold transition"
                  >
                    {downloadingId === b.id ? (
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3M4 19h16"/>
                      </svg>
                    )}
                    تنزيل PDF
                  </button>
                  <button
                    onClick={() => dismissConfirmed(b.id)}
                    className="text-emerald-500 hover:text-emerald-700 p-1 rounded-lg hover:bg-emerald-100 transition"
                    title="إغلاق"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="text-center text-gray-400 py-16">جاري التحميل...</div>
      ) : bookings.length === 0 ? (
        <div className="text-center bg-white rounded-xl shadow p-16 text-gray-400">
          <p className="text-4xl mb-3">✅</p>
          <p>لا توجد حجوزات بانتظار المراجعة</p>
        </div>
      ) : (
        <div className="space-y-4">
          {bookings.map((booking) => (
            <div key={booking.id} className="bg-white rounded-xl shadow p-5">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-800">{booking.bookingNumber}</span>
                    <span className="bg-yellow-100 text-yellow-700 text-xs px-2 py-1 rounded-full">بانتظار التأكيد</span>
                  </div>
                  {booking.passengerDetails && booking.passengerDetails.length > 0 ? (
                    booking.passengerDetails.map((pd, pdi) => (
                      <p key={pdi} className="text-gray-600">
                        {booking.passengerDetails.length > 1 ? `مسافر ${pdi + 1}: ` : 'المسافر: '}
                        <span className="font-medium">{pd.name}</span>
                        {' — '}{pd.phone}
                        {' — مقعد '}<span className="font-medium">{pd.seat}</span>
                      </p>
                    ))
                  ) : (
                    <>
                      <p className="text-gray-600">المسافر: <span className="font-medium">{booking.passenger?.name}</span></p>
                      <p className="text-gray-600">الهاتف: {booking.passenger?.phone}</p>
                    </>
                  )}
                  <p className="text-gray-600">
                    الرحلة: {booking.trip?.fromCity} → {booking.trip?.toCity}
                  </p>
                  <p className="text-gray-600">
                    الموعد: {booking.trip?.departureTime ? format(new Date(booking.trip.departureTime), 'yyyy/MM/dd HH:mm') : '-'}
                  </p>
                  <p className="text-gray-600">المقاعد: {booking.seats?.join(', ')}</p>
                  <p className="font-semibold text-blue-700">
                    المبلغ: {parseFloat(booking.totalAmount).toLocaleString()} جنيه
                    {booking.promoCode && (
                      <span className="mr-2 text-xs font-normal bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                        🏷️ {booking.promoCode} −{booking.discountPercent}%
                      </span>
                    )}
                  </p>
                </div>

                <div className="flex flex-col gap-3 items-end">
                  {booking.paymentProofUrl && (
                    <button
                      onClick={() => openReceipt(booking.paymentProofUrl)}
                      className="flex items-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm transition"
                    >
                      📎 عرض الإيصال
                    </button>
                  )}
                  <button
                    onClick={() => handleConfirm(booking.id)}
                    className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg transition"
                  >
                    ✅ تأكيد الدفع وإصدار التذكرة
                  </button>
                  <button
                    onClick={() => openRejectModal(booking)}
                    className="bg-red-100 hover:bg-red-200 text-red-700 px-5 py-2 rounded-lg transition"
                  >
                    ❌ رفض الإيصال
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4" dir="rtl">
            <h2 className="text-lg font-bold text-gray-800 mb-3">سبب الرفض</h2>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-3 text-sm resize-none"
              rows={4}
              placeholder="اكتب سبب رفض الإيصال..."
            />
            <div className="flex gap-3 mt-4">
              <button onClick={handleReject} className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700">
                تأكيد الرفض
              </button>
              <button onClick={() => setShowRejectModal(false)} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200">
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Office booking modal ── */}
      {showOfficeModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col" dir="rtl">
            {/* Header */}
            <div className="p-5 border-b flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-lg font-bold text-gray-800">🏢 حجز مكتبي</h2>
                <p className="text-xs text-gray-400 mt-0.5">للمسافرين الحاضرين في المكتب — يُؤكَّد فوراً</p>
              </div>
              <button onClick={() => setShowOfficeModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
            </div>

            <div className="overflow-y-auto flex-1 p-5 space-y-5">
              {/* Trip selector */}
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-1.5">اختر الرحلة</label>
                {officeTripsLoading ? (
                  <p className="text-gray-400 text-sm">جاري تحميل الرحلات...</p>
                ) : officeTrips.length === 0 ? (
                  <p className="text-amber-600 text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    لا توجد رحلات مجدولة بها مقاعد متاحة
                  </p>
                ) : (
                  <select
                    value={officeForm.tripId}
                    onChange={(e) => handleTripSelect(e.target.value)}
                    className="border border-gray-300 rounded-xl px-3 py-2.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-green-400"
                  >
                    <option value="">-- اختر رحلة --</option>
                    {officeTrips.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.fromCity} ← {t.toCity} &nbsp;|&nbsp; {format(new Date(t.departureTime), 'yyyy/MM/dd HH:mm')} &nbsp;|&nbsp; {t.availableSeats} مقعد متاح &nbsp;|&nbsp; {parseFloat(t.price).toLocaleString()} ج
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Seat count quick-input */}
              {selectedTripData && (
                <div>
                  <label className="text-sm font-semibold text-gray-700 block mb-1.5">
                    عدد المقاعد
                    <span className="mr-2 text-gray-400 font-normal text-xs">(متاح: {selectedTripData.availableSeats})</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    max={selectedTripData.availableSeats}
                    value={officeForm.seatCount}
                    onChange={(e) => handleSeatCount(e.target.value)}
                    placeholder="أدخل عدد المقاعد"
                    className="border border-gray-300 rounded-xl px-3 py-2.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-green-400"
                  />
                  {officeForm.seats.length > 0 && (
                    <p className="text-xs text-green-600 mt-1">
                      المقاعد المختارة: {officeForm.seats.join('، ')} — المبلغ: {(parseFloat(selectedTripData.price) * officeForm.seats.length).toLocaleString()} ج
                    </p>
                  )}
                </div>
              )}

              {/* Seat grid */}
              {selectedTripData && (
                <div>
                  <label className="text-sm font-semibold text-gray-700 block mb-2">
                    تعديل المقاعد يدوياً
                  </label>
                  <div className="grid grid-cols-7 gap-1.5">
                    {Array.from({ length: selectedTripData.bus?.totalSeats || 43 }, (_, i) => i + 1).map((seatNum) => {
                      const isBooked   = selectedTripData.bookedSeats?.includes(seatNum);
                      const isSelected = officeForm.seats.includes(seatNum);
                      return (
                        <button
                          key={seatNum}
                          disabled={isBooked}
                          onClick={() => !isBooked && toggleOfficeSeat(seatNum)}
                          className={`py-1.5 rounded-lg text-xs font-bold border transition
                            ${isBooked
                              ? 'bg-red-50 text-red-300 border-red-200 cursor-not-allowed'
                              : isSelected
                              ? 'bg-green-600 text-white border-green-600 shadow-sm'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-green-400 hover:bg-green-50'}`}
                        >
                          {seatNum}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex gap-4 mt-2 text-[11px]">
                    <span className="flex items-center gap-1"><span className="w-3 h-3 bg-white border border-gray-300 rounded inline-block" />متاح</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-600 rounded inline-block" />مختار</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-50 border border-red-200 rounded inline-block" />محجوز</span>
                  </div>
                </div>
              )}

              {/* Per-seat passenger details */}
              {officeForm.passengerDetails.length > 0 && (
                <div>
                  <label className="text-sm font-semibold text-gray-700 block mb-2">بيانات المسافرين</label>
                  <div className="space-y-3">
                    {officeForm.passengerDetails.map((pd) => (
                      <div key={pd.seat} className="bg-gray-50 rounded-xl px-3 py-3 space-y-2 border border-gray-100">
                        <div className="flex items-center gap-2">
                          <span className="bg-green-600 text-white font-bold text-xs px-2.5 py-1 rounded-lg flex-shrink-0">مقعد {pd.seat}</span>
                          <input
                            type="text"
                            placeholder="الاسم رباعياً (مثال: أحمد محمد علي حسن)"
                            value={pd.name}
                            onChange={(e) => updateOfficePassenger(pd.seat, 'name', e.target.value)}
                            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                          />
                        </div>
                        <div className="flex gap-2">
                          <input
                            type="tel"
                            placeholder="رقم الهاتف"
                            value={pd.phone}
                            onChange={(e) => updateOfficePassenger(pd.seat, 'phone', e.target.value)}
                            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                            dir="ltr"
                          />
                          <input
                            type="tel"
                            placeholder="رقم الواتساب"
                            value={pd.whatsapp || ''}
                            onChange={(e) => updateOfficePassenger(pd.seat, 'whatsapp', e.target.value)}
                            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                            dir="ltr"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-5 border-t flex-shrink-0 flex gap-3">
              <button
                onClick={handleOfficeSubmit}
                disabled={officeSubmitting || officeForm.seats.length === 0}
                className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-2.5 rounded-xl font-semibold text-sm transition"
              >
                {officeSubmitting ? 'جاري الحفظ...' : '✅ تأكيد الحجز المكتبي'}
              </button>
              <button
                onClick={() => setShowOfficeModal(false)}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 rounded-xl text-sm"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingsPage;
